// SPDX-License-Identifier: AGPL-3.0-only

import { requestJson } from "./http.js";

export type Capability =
  | "system.overview.read"
  | "system.settings.manage"
  | "system.users.manage"
  | "tenant.read"
  | "tenant.create"
  | "tenant.delete"
  | "tenant.lifecycle.manage"
  | "tenant.settings.manage"
  | "tenant.members.manage"
  | "tenant.notifications.read"
  | "tenant.notifications.manage"
  | "probe.read"
  | "probe.configuration.manage"
  | "probe.operations.execute"
  | "probe.enrollment.manage"
  | "probe.credentials.rotate"
  | "probe.lifecycle.manage"
  | "measurement.read";

export type TenantRole = "tenant_admin" | "probe_admin" | "operator" | "viewer";
export type TenantStatus = "active" | "suspended" | "disabled";
export type TenantActivationStatus = "active" | "disabled";

export interface TenantAccess {
  readonly systemAdministrator: boolean;
  readonly role: TenantRole | null;
  readonly capabilities: readonly Capability[];
}

export interface TenantTierSummary {
  readonly id: string;
  readonly name: string;
  readonly status: "active" | "disabled";
  readonly legacy: boolean;
  readonly maxProbesPerTenant: number;
  readonly maxUsersPerTenant: number;
  readonly maxNotificationContactsPerTenant: number;
}

export interface TenantSummary {
  readonly id: string;
  readonly slug: string;
  readonly displayName: string;
  readonly status: TenantStatus;
  readonly tier: TenantTierSummary;
  readonly access: TenantAccess;
}

export interface CreateTenantInput {
  readonly slug: string;
  readonly displayName: string;
  readonly tierId: string;
}

export interface RenameTenantInput {
  readonly displayName: string;
}

const CAPABILITIES: ReadonlySet<string> = new Set<string>([
  "system.overview.read",
  "system.settings.manage",
  "system.users.manage",
  "tenant.read",
  "tenant.create",
  "tenant.delete",
  "tenant.lifecycle.manage",
  "tenant.settings.manage",
  "tenant.members.manage",
  "tenant.notifications.read",
  "tenant.notifications.manage",
  "probe.read",
  "probe.configuration.manage",
  "probe.operations.execute",
  "probe.enrollment.manage",
  "probe.credentials.rotate",
  "probe.lifecycle.manage",
  "measurement.read",
]);
const TENANT_ROLES: ReadonlySet<string> = new Set<string>([
  "tenant_admin",
  "probe_admin",
  "operator",
  "viewer",
]);
const TENANT_STATUSES: ReadonlySet<string> = new Set<string>(["active", "suspended", "disabled"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(value: unknown, fieldName: string): string {
  if (typeof value !== "string") {
    throw new Error(`Tenant response field ${fieldName} is invalid`);
  }
  return value;
}

function readTenant(payload: unknown, fieldName: string): TenantSummary {
  if (!isRecord(payload) || !isRecord(payload.access) || !isRecord(payload.tier)) {
    throw new Error(`Tenant response field ${fieldName} is invalid`);
  }
  if (typeof payload.access.systemAdministrator !== "boolean") {
    throw new Error(`Tenant response field ${fieldName}.access.systemAdministrator is invalid`);
  }

  const status: string = readString(payload.status, `${fieldName}.status`);
  const role: unknown = payload.access.role;
  const capabilities: unknown = payload.access.capabilities;
  if (!TENANT_STATUSES.has(status)) {
    throw new Error(`Tenant response field ${fieldName}.status is invalid`);
  }
  const tierStatus: unknown = payload.tier.status;
  if (tierStatus !== "active" && tierStatus !== "disabled") {
    throw new Error(`Tenant response field ${fieldName}.tier.status is invalid`);
  }
  if (typeof payload.tier.legacy !== "boolean") {
    throw new Error(`Tenant response field ${fieldName}.tier.legacy is invalid`);
  }
  if (
    typeof payload.tier.maxProbesPerTenant !== "number" ||
    !Number.isInteger(payload.tier.maxProbesPerTenant)
  ) {
    throw new Error(`Tenant response field ${fieldName}.tier.maxProbesPerTenant is invalid`);
  }
  if (
    typeof payload.tier.maxUsersPerTenant !== "number" ||
    !Number.isInteger(payload.tier.maxUsersPerTenant)
  ) {
    throw new Error(`Tenant response field ${fieldName}.tier.maxUsersPerTenant is invalid`);
  }
  if (
    typeof payload.tier.maxNotificationContactsPerTenant !== "number" ||
    !Number.isInteger(payload.tier.maxNotificationContactsPerTenant)
  ) {
    throw new Error(`Tenant response field ${fieldName}.tier.maxNotificationContactsPerTenant is invalid`);
  }
  if (role !== null && (typeof role !== "string" || !TENANT_ROLES.has(role))) {
    throw new Error(`Tenant response field ${fieldName}.access.role is invalid`);
  }
  if (
    !Array.isArray(capabilities) ||
    !capabilities.every(
      (capability: unknown): boolean =>
        typeof capability === "string" && CAPABILITIES.has(capability),
    )
  ) {
    throw new Error(`Tenant response field ${fieldName}.access.capabilities is invalid`);
  }

  return {
    id: readString(payload.id, `${fieldName}.id`),
    slug: readString(payload.slug, `${fieldName}.slug`),
    displayName: readString(payload.displayName, `${fieldName}.displayName`),
    status: status as TenantStatus,
    tier: {
      id: readString(payload.tier.id, `${fieldName}.tier.id`),
      name: readString(payload.tier.name, `${fieldName}.tier.name`),
      status: tierStatus,
      legacy: payload.tier.legacy,
      maxProbesPerTenant: payload.tier.maxProbesPerTenant,
      maxUsersPerTenant: payload.tier.maxUsersPerTenant,
      maxNotificationContactsPerTenant: payload.tier.maxNotificationContactsPerTenant,
    },
    access: {
      systemAdministrator: payload.access.systemAdministrator,
      role: role as TenantRole | null,
      capabilities: capabilities as Capability[],
    },
  };
}

export async function listTenants(): Promise<readonly TenantSummary[]> {
  const payload: unknown = await requestJson("/api/v1/tenants");
  if (!isRecord(payload) || !Array.isArray(payload.tenants)) {
    throw new Error("Tenant list response is invalid");
  }

  return payload.tenants.map(
    (tenant: unknown, index: number): TenantSummary => readTenant(tenant, `tenants.${index}`),
  );
}

export async function getTenant(tenantId: string): Promise<TenantSummary> {
  const payload: unknown = await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}`);
  return readTenant(payload, "tenant");
}

export async function createTenant(input: CreateTenantInput): Promise<TenantSummary> {
  const payload: unknown = await requestJson("/api/v1/tenants", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return readTenant(payload, "tenant");
}

export async function deleteTenant(tenantId: string): Promise<void> {
  await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}`, { method: "DELETE" });
}

export async function renameTenant(
  tenantId: string,
  input: RenameTenantInput,
): Promise<TenantSummary> {
  const payload: unknown = await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  return readTenant(payload, "tenant");
}

export async function changeTenantStatus(
  tenantId: string,
  status: TenantActivationStatus,
): Promise<TenantSummary> {
  const payload: unknown = await requestJson(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/status`,
    { method: "PATCH", body: JSON.stringify({ status }) },
  );
  return readTenant(payload, "tenant");
}

export async function assignTenantTier(tenantId: string, tierId: string): Promise<void> {
  await requestJson(`/api/v1/tenants/${encodeURIComponent(tenantId)}/tier`, {
    method: "PATCH",
    body: JSON.stringify({ tierId }),
  });
}
